import styled from '@emotion/styled';
import _range from 'lodash/range';
import React from 'react';

import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color } from 'src/shared/styles/variables';

import * as S from './styles';

interface CircularRateExtendedProps extends S.CircularRateProps {
    highlighted?: boolean;
}

const CircularRateExtended = styled(S.CircularRate)<CircularRateExtendedProps>(
    ({ selected, highlighted }) =>
        !selected && {
            cursor: 'pointer',
            '&, &:hover': {
                backgroundColor: highlighted ? color.springGreen : hexToRgba(color.scooter, 0.2),
                color: highlighted ? color.white : color.scooter,
            },
        },
);

interface Props {
    range: number;
    initialRate: number;
    additionalRate: number;
    availablePoints: number;
    onRateExtend: (rate: number) => void;
}

interface State {
    hoveredRate: number | undefined;
}

export class CircularRatesExtended extends React.Component<Props, State> {
    public state: State = {
        hoveredRate: undefined,
    };

    private setHoveredRate = (rate?: number) => () => {
        if (rate === undefined || rate > this.props.initialRate) {
            this.setState({
                hoveredRate: rate,
            });
        }
    };

    private isRateHighlighted = (rate: number): boolean => {
        const { initialRate, additionalRate } = this.props;
        const { hoveredRate } = this.state;

        const difference = rate - initialRate;

        if (difference <= 0) {
            return false;
        }

        return hoveredRate ? rate <= hoveredRate : difference <= additionalRate;
    };

    private handleRateClick = (rate: number) => (): void => {
        const { initialRate, additionalRate, availablePoints } = this.props;
        const difference = rate - initialRate;

        if (difference === additionalRate) {
            return this.props.onRateExtend(difference - 1);
        }

        if (difference <= additionalRate + availablePoints) {
            return this.props.onRateExtend(difference);
        }
    };

    private handleCircularRatesMouseLeave = () => this.setHoveredRate();

    public render(): JSX.Element {
        const { range, initialRate } = this.props;

        return (
            <S.CircularRatesWrapper>
                <S.CircularRatesWrapper onMouseLeave={this.handleCircularRatesMouseLeave()}>
                    {_range(1, range + 1).map(rate => (
                        <CircularRateExtended
                            selected={rate <= initialRate}
                            highlighted={this.isRateHighlighted(rate)}
                            onMouseOver={this.setHoveredRate(rate)}
                            onClick={this.handleRateClick(rate)}
                            key={rate}
                        >
                            {rate}
                        </CircularRateExtended>
                    ))}
                </S.CircularRatesWrapper>
            </S.CircularRatesWrapper>
        );
    }
}
